import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Search, Map, Navigation, Loader } from 'lucide-react';

interface AddressData {
  address: string;
  city: string;
  postal_code: string;
  country: string;
  maps_link: string;
}

interface OpenStreetMapAddressPickerProps {
  onAddressSelect: (addressData: AddressData) => void;
  initialAddress?: string;
  initialCity?: string;
  initialPostalCode?: string;
  initialCountry?: string;
  initialMapsLink?: string;
  onInputChange?: (value: string) => void;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    postcode?: string;
    country?: string;
    state?: string;
    amenity?: string;
    tourism?: string;
    aeroway?: string;
    shop?: string;
    leisure?: string;
    historic?: string;
  };
}

const OpenStreetMapAddressPicker: React.FC<OpenStreetMapAddressPickerProps> = ({
  onAddressSelect,
  initialAddress = '',
  initialCity = '',
  initialPostalCode = '',
  initialCountry: _initialCountry = 'France',
  initialMapsLink = '',
  onInputChange
}) => {
  const [currentAddress, setCurrentAddress] = useState(initialAddress);
  const [currentMapsLink, setCurrentMapsLink] = useState(initialMapsLink);
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number, lng: number } | null>(null);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualCity, setManualCity] = useState(initialCity);
  const [manualPostalCode, setManualPostalCode] = useState(initialPostalCode);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync state when initial values change (e.g., when editing existing client)
  useEffect(() => {
    setCurrentAddress(initialAddress);
    setCurrentMapsLink(initialMapsLink);
    setManualCity(initialCity);
    setManualPostalCode(initialPostalCode);
  }, [initialAddress, initialCity, initialPostalCode, initialMapsLink]);

  // Initialize Leaflet map when showMap becomes true
  useEffect(() => {
    if (showMap && mapRef.current && !leafletMapRef.current) {
      // Load Leaflet dynamically
      const loadLeaflet = async () => {
        try {
          // Load Leaflet CSS
          if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
          }

          // Load Leaflet JS
          if (!(window as any).L) {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }

          const L = (window as any).L;

          // Initialize map centered on selected location if available, otherwise Paris (default fallback)
          const PARIS_COORDS = [48.8566, 2.3522];
          const initialCenter = selectedLocation
            ? [selectedLocation.lat, selectedLocation.lng]
            : PARIS_COORDS;
          const initialZoom = selectedLocation ? 15 : 12;
          const map = L.map(mapRef.current).setView(initialCenter, initialZoom);

          // Add OpenStreetMap tiles
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(map);

          // Add click handler for map
          map.on('click', async (e: any) => {
            const { lat, lng } = e.latlng;
            setSelectedLocation({ lat, lng });

            // Reverse geocode the clicked location
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
              );

              if (response.ok) {
                const result = await response.json();
                if (result.display_name) {
                  const addressComponents = result.address || {};
                  const address = result.display_name;
                  const city = addressComponents.city || addressComponents.town || addressComponents.village || '';
                  const postal_code = addressComponents.postcode || '';
                  const country = addressComponents.country || 'France';
                  const encodedAddress = encodeURIComponent(address);
                  const maps_link = `https://www.google.com/maps/search/${encodedAddress}/@${lat},${lng},15z`;

                  const addressData: AddressData = {
                    address,
                    city,
                    postal_code,
                    country,
                    maps_link
                  };

                  setCurrentAddress(address);
                  setCurrentMapsLink(maps_link);
                  onAddressSelect(addressData);

                  // Add marker to map
                  L.marker([lat, lng]).addTo(map)
                    .bindPopup(address)
                    .openPopup();
                }
              }
            } catch (error) {
              console.error('Reverse geocoding failed:', error);
            }
          });

          leafletMapRef.current = map;

          // If we already have a selected location (address picked before opening the map),
          // show it immediately on the map.
          if (selectedLocation) {
            L.marker([selectedLocation.lat, selectedLocation.lng]).addTo(map);
          }
        } catch (error) {
          console.error('Failed to load Leaflet:', error);
        }
      };

      loadLeaflet();
    }

    // Cleanup map when component unmounts or showMap becomes false
    return () => {
      if (leafletMapRef.current && !showMap) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [showMap, onAddressSelect]);

  // When a location is selected, ensure the map is centered and shows a marker
  // (covers the case where the user selects an address, then opens the map).
  useEffect(() => {
    if (!showMap || !leafletMapRef.current || !selectedLocation) return;

    const L = (window as any).L;
    if (!L) return;

    leafletMapRef.current.setView([selectedLocation.lat, selectedLocation.lng], 15);

    // Clear existing markers and add new one
    leafletMapRef.current.eachLayer((layer: any) => {
      if (layer instanceof L.Marker) {
        leafletMapRef.current.removeLayer(layer);
      }
    });

    L.marker([selectedLocation.lat, selectedLocation.lng]).addTo(leafletMapRef.current);
  }, [selectedLocation, showMap]);

  // Attempt to auto-geolocate when map is first shown if no location is selected
  useEffect(() => {
    if (showMap && !selectedLocation && !isGeolocating) {
      // Small delay to ensure map is ready and let the user see the default view first
      const timer = setTimeout(() => {
        getCurrentLocation(true); // silent = true
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [showMap]);

  const searchAddresses = async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Enhanced search: Try both place names and addresses
      // First, search for places/POIs (Points of Interest)
      const placeResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=10&countrycodes=fr&q=${encodeURIComponent(query)}`
      );

      // Then search with country restrictions for more local results
      const localResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=10&countrycodes=fr&q=${encodeURIComponent(query)}`
      );

      const placeResults: NominatimResult[] = placeResponse.ok ? await placeResponse.json() : [];
      const localResults: NominatimResult[] = localResponse.ok ? await localResponse.json() : [];

      // Combine and deduplicate results
      const allResults = [...placeResults, ...localResults];
      const uniqueResults = allResults.filter((result, index, self) =>
        index === self.findIndex(r => r.lat === result.lat && r.lon === result.lon)
      );

      // Sort by relevance (places/POIs first, then addresses)
      const sortedResults = uniqueResults.sort((a, b) => {
        const aIsPlace = a.display_name.toLowerCase().includes(query.toLowerCase()) &&
          (a.address?.amenity || a.address?.tourism || a.address?.aeroway);
        const bIsPlace = b.display_name.toLowerCase().includes(query.toLowerCase()) &&
          (b.address?.amenity || b.address?.tourism || b.address?.aeroway);

        if (aIsPlace && !bIsPlace) return -1;
        if (!aIsPlace && bIsPlace) return 1;
        return 0;
      });

      setSearchResults(sortedResults.slice(0, 5));
      setShowResults(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentAddress(value);
    if (onInputChange) {
      onInputChange(value);
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  const selectAddress = (result: NominatimResult) => {
    const address = result.display_name;
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Extract address components
    const addressComponents = result.address;
    const city = addressComponents.city || addressComponents.town || addressComponents.village || '';
    const postal_code = addressComponents.postcode || '';
    const country = addressComponents.country || 'France';

    // Generate Google Maps link with marker using query parameter
    const encodedAddress = encodeURIComponent(address);
    const maps_link = `https://www.google.com/maps/search/${encodedAddress}/@${lat},${lng},15z`;

    const addressData: AddressData = {
      address,
      city,
      postal_code,
      country,
      maps_link
    };

    setCurrentAddress(address);
    setCurrentMapsLink(maps_link);
    setSelectedLocation({ lat, lng });
    setShowResults(false);
    setSearchResults([]);
    onAddressSelect(addressData);

    // If map is visible, center it on the selected location and add marker
    if (leafletMapRef.current) {
      const L = (window as any).L;
      leafletMapRef.current.setView([lat, lng], 15);

      // Clear existing markers and add new one
      leafletMapRef.current.eachLayer((layer: any) => {
        if (layer instanceof L.Marker) {
          leafletMapRef.current.removeLayer(layer);
        }
      });

      L.marker([lat, lng]).addTo(leafletMapRef.current)
        .bindPopup(address)
        .openPopup();
    }
  };

  const getCurrentLocation = (silent = false) => {
    if (!navigator.geolocation) {
      if (!silent) alert('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsGeolocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        setSelectedLocation({ lat, lng });

        try {
          // Reverse geocode the current location
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
          );

          if (response.ok) {
            const result = await response.json();
            if (result.display_name) {
              const addressComponents = result.address || {};
              const address = result.display_name;
              const city = addressComponents.city || addressComponents.town || addressComponents.village || '';
              const postal_code = addressComponents.postcode || '';
              const country = addressComponents.country || 'France';
              const encodedAddress = encodeURIComponent(address);
              const maps_link = `https://www.google.com/maps/search/${encodedAddress}/@${lat},${lng},15z`;

              const addressData: AddressData = {
                address,
                city,
                postal_code,
                country,
                maps_link
              };

              setCurrentAddress(address);
              setCurrentMapsLink(maps_link);
              onAddressSelect(addressData);

              // If map is visible, center it on the current location and add marker
              if (leafletMapRef.current) {
                const L = (window as any).L;
                leafletMapRef.current.setView([lat, lng], 15);

                // Clear existing markers and add new one
                leafletMapRef.current.eachLayer((layer: any) => {
                  if (layer instanceof L.Marker) {
                    leafletMapRef.current.removeLayer(layer);
                  }
                });

                L.marker([lat, lng]).addTo(leafletMapRef.current)
                  .bindPopup('📍 Votre position actuelle')
                  .openPopup();
              }
            }
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          if (!silent) alert('Impossible de récupérer l\'adresse de votre position');
        } finally {
          setIsGeolocating(false);
        }
      },
      (error) => {
        setIsGeolocating(false);
        if (silent) return;

        let message = 'Impossible de récupérer votre position';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permission de géolocalisation refusée';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Position non disponible';
            break;
          case error.TIMEOUT:
            message = 'Délai de géolocalisation dépassé';
            break;
        }

        alert(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Function to use manually entered address
  const useManualAddress = () => {
    if (currentAddress.length < 3) return;

    // Create address data from manual input
    const addressData: AddressData = {
      address: currentAddress,
      city: manualCity,
      postal_code: manualPostalCode,
      country: 'France',
      maps_link: `https://www.google.com/maps/search/${encodeURIComponent(currentAddress + ' ' + manualCity + ' ' + manualPostalCode)}`
    };

    setCurrentMapsLink(addressData.maps_link);
    setShowResults(false);
    setSearchResults([]);
    setShowManualForm(false);
    setManualCity('');
    setManualPostalCode('');
    onAddressSelect(addressData);
  };

  // Close results when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node) && !containerRef.current?.contains(e.target as Node)) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [showResults]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium mb-1">
          <MapPin className="inline h-4 w-4 mr-1" />
          Adresse
        </label>
        <button
          type="button"
          onClick={() => setShowMap(!showMap)}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${showMap
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600'
            }`}
          title={showMap ? 'Masquer la carte' : 'Afficher la carte'}
        >
          <Map className="h-3 w-3" />
          {showMap ? 'Masquer' : 'Carte'}
        </button>
      </div>

      <div className="relative" ref={containerRef}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={currentAddress}
              onChange={handleInputChange}
              placeholder="Tapez une adresse ou un lieu (ex: Jijel Airport)..."
              className="w-full pl-10 pr-10 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <button
            type="button"
            onClick={() => getCurrentLocation(false)}
            disabled={isGeolocating}
            className={`flex items-center justify-center px-3 py-2 border rounded-lg transition-colors ${isGeolocating
              ? 'bg-slate-100 border-slate-300 cursor-not-allowed dark:bg-slate-800 dark:border-slate-600'
              : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            title="Utiliser ma position actuelle"
          >
            {isGeolocating ? (
              <Loader className="h-4 w-4 animate-spin text-slate-400" />
            ) : (
              <Navigation className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            )}
          </button>
        </div>

        {/* Search Results Dropdown */}
        {showResults && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg"
          >
            {isSearching && (
              <div className="p-3 text-center text-slate-500 dark:text-slate-400">
                <Search className="h-4 w-4 animate-spin mx-auto mb-1" />
                Recherche...
              </div>
            )}

            {showManualForm && !isSearching && (
              <div className="p-3" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={manualCity}
                    onChange={(e) => setManualCity(e.target.value)}
                    placeholder="Ville"
                    className="w-full px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={manualPostalCode}
                    onChange={(e) => setManualPostalCode(e.target.value)}
                    placeholder="Code postal"
                    className="w-full px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowManualForm(false)}
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={useManualAddress}
                      className="flex-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Valider
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!showManualForm && !isSearching && (
              <>
                {searchResults.length === 0 && currentAddress.length >= 3 && (
                  <div className="p-3 text-center">
                    <p className="text-slate-500 dark:text-slate-400 mb-3">Aucune adresse trouvée</p>
                    <button
                      type="button"
                      onClick={() => setShowManualForm(true)}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Ajouter manuellement
                    </button>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <>
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => selectAddress(result)}
                        className="w-full p-3 text-left hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-slate-200 dark:border-slate-600 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {result.display_name}
                            </p>
                            {result.address && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {result.address.postcode} {result.address.city || result.address.town || result.address.village}, {result.address.country}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                    <div className="p-3 border-t border-slate-200 dark:border-slate-600 text-center bg-white dark:bg-slate-800">
                      <button
                        type="button"
                        onClick={() => setShowManualForm(true)}
                        className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        Ajouter manuellement
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Interactive Map */}
      {showMap && (
        <div className="mt-3 border border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-50 dark:bg-slate-800 px-3 py-2 border-b border-slate-300 dark:border-slate-600">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Cliquez sur la carte pour sélectionner une adresse
              </p>
              {selectedLocation && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  📍 {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                </p>
              )}
            </div>
          </div>
          <div
            ref={mapRef}
            className="w-full h-64 bg-slate-100 dark:bg-slate-800"
            style={{ minHeight: '256px' }}
          />
        </div>
      )}

      {currentMapsLink && (
        <p className="text-xs text-green-600 dark:text-green-400">
          ✓ Adresse sélectionnée • Lien Google Maps généré
        </p>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Powered by OpenStreetMap • Lien Google Maps généré automatiquement
      </p>
    </div>
  );
};

export default OpenStreetMapAddressPicker;
